// Project-I/O met afgeleide fase. Onderdeel van de additive api-split (story 1.6).
// Verzamelt per project de PhaseInput (markers + definitief-count + PO-statussen) in
// bulk, zodat de lijst de afgeleide fase kan tonen zonder N+1 queries.
import { supabase } from '../supabase';
import type { PhaseInput } from '../projectPhase';
import type { Client, Project } from '../types';

export type ProjectWithPhase = Project & {
  client?: Client | null;
  phaseInput: PhaseInput;
};

type PoStatus = PhaseInput['purchaseOrderStatuses'][number];

export async function fetchProjectsWithPhase(): Promise<ProjectWithPhase[]> {
  const { data: projects, error } = await supabase
    .from('projects')
    .select('*, client:clients(id, company_name, contact_name, contact_email)')
    .eq('type', 'package')
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;

  const rows = (projects as unknown as (Project & { client?: Client | null })[]) ?? [];
  if (rows.length === 0) return [];

  const projectIds = rows.map((p) => p.id);

  // Pakketten: definitief-count per project + package_id -> project_id map.
  const { data: packages } = await supabase
    .from('gift_packages')
    .select('id, project_id, variant_status')
    .in('project_id', projectIds);

  const definitiefCount = new Map<string, number>();
  const packageToProject = new Map<string, string>();
  for (const pkg of packages ?? []) {
    if (!pkg.project_id) continue;
    packageToProject.set(pkg.id, pkg.project_id);
    if (pkg.variant_status === 'definitief') {
      definitiefCount.set(pkg.project_id, (definitiefCount.get(pkg.project_id) ?? 0) + 1);
    }
  }

  // Inkooporders: status per project (via package).
  const packageIds = [...packageToProject.keys()];
  const poByProject = new Map<string, PoStatus[]>();
  if (packageIds.length > 0) {
    const { data: pos } = await supabase
      .from('gift_purchase_orders')
      .select('package_id, status')
      .in('package_id', packageIds);
    for (const po of pos ?? []) {
      const projectId = po.package_id ? packageToProject.get(po.package_id) : undefined;
      if (!projectId) continue;
      const list = poByProject.get(projectId) ?? [];
      list.push(po.status as PoStatus);
      poByProject.set(projectId, list);
    }
  }

  return enrich(rows, definitiefCount, poByProject);
}

function enrich(
  rows: (Project & { client?: Client | null })[],
  definitiefCount: Map<string, number>,
  poByProject: Map<string, PoStatus[]>
): ProjectWithPhase[] {
  return rows.map((p) => ({
    ...p,
    phaseInput: {
      offerteSentAt: p.offerte_sent_at,
      akkoordAt: p.akkoord_at,
      afgerondAt: p.afgerond_at,
      definitiefCount: definitiefCount.get(p.id) ?? 0,
      purchaseOrderStatuses: poByProject.get(p.id) ?? [],
    },
  }));
}

/** PhaseInput voor één project (detailweergave): markers + definitief-count uit de al
 *  geladen pakketten + PO-statussen via één query. */
export async function fetchPhaseInput(
  project: Pick<Project, 'offerte_sent_at' | 'akkoord_at' | 'afgerond_at'>,
  packages: Array<{ id: string; variant_status: string | null }>
): Promise<PhaseInput> {
  const definitiefCount = packages.filter((p) => p.variant_status === 'definitief').length;
  const packageIds = packages.map((p) => p.id);
  let purchaseOrderStatuses: PoStatus[] = [];
  if (packageIds.length > 0) {
    const { data: pos } = await supabase
      .from('gift_purchase_orders')
      .select('status')
      .in('package_id', packageIds);
    purchaseOrderStatuses = (pos ?? []).map((po) => po.status as PoStatus);
  }
  return {
    offerteSentAt: project.offerte_sent_at,
    akkoordAt: project.akkoord_at,
    afgerondAt: project.afgerond_at,
    definitiefCount,
    purchaseOrderStatuses,
  };
}
